import styles from './styles.module.scss';
import { ModalProps, Modal, AutoComplete, Option } from 'components-antd';
import { CloseModalIconSmall } from 'pages/ServicesCategory/components/AddVendorModal/icons';
import { Button, Avatar } from 'components';
import { useCallback, useEffect, useState } from 'react';
import { Icons } from 'pages/Profile/components/icons';
import { useDispatch, useSelector } from 'react-redux';
import { getActiveTeamList } from 'store/selectors/teamList';
import { Cross } from 'components/Icons';
import { reassignTasksInBulk } from 'api/workshop/tasks';
import { getTransactionRolesEffect } from 'store/effects';
import { getTransactionRolesSelector } from 'store/selectors/transactionRoles';
import { PreparedTaskType, TaskReassignmentPayloadType, TaskTransactionAccess } from '../types';
import {
  caseInsensitiveFilter,
  convertNameToAvatarPlaceholder,
  showErrorMessage,
  showSuccessMessage,
} from 'helpers';

interface ReassignTaskModalPropsType extends ModalProps {
  handleClose: (boolean) => void;
  tasks: PreparedTaskType[];
  contactId: number;
}

const ReassignTaskModal = (props: ReassignTaskModalPropsType) => {
  const dispatch = useDispatch();
  const { open, handleClose, tasks, contactId } = props;
  const [reassigning, setReassigning] = useState<boolean>(false);
  const [assignees, setAssignees] = useState<number[]>([]);
  const activeTeamList = useSelector(getActiveTeamList);
  const teamListWithoutContact = activeTeamList.filter((member) => member.id !== contactId);
  const [inputVal, setInputVal] = useState<string>('');
  const [memberAccesses, setMemberAccesses] = useState<TaskTransactionAccess[]>([]);
  const { transactionRoles } = useSelector(getTransactionRolesSelector);
  const trxCoordinatorRoleId = transactionRoles?.find(
    (role) => role.Title === 'TransactionCoordinator',
  )?.Id;

  useEffect(() => {
    dispatch(getTransactionRolesEffect());
  }, []);

  useEffect(() => {
    let accesses: TaskTransactionAccess[] = assignees.map((item) => ({
      MemberId: item,
      Tasks: [],
    }));

    tasks.forEach((task) => {
      accesses = accesses.map((item) => {
        const alreadyExits = memberAccesses.find((member) => member.MemberId === item.MemberId);

        if (alreadyExits) return alreadyExits;

        const participantFound = task.Transaction.Participants?.find(
          (participant) => participant.ParticipantId === item.MemberId,
        );

        return {
          ...item,
          Tasks: [
            ...item.Tasks,
            {
              TaskId: task.Id,
              TransactionId: task.Transaction.Id,
              FullAccess: participantFound ? participantFound.TransactionAccess : null,
              AddAsParticipant: participantFound ? false : true,
              RoleId: participantFound ? 0 : null,
              TransactionName: task.TransactionName,
            },
          ],
        };
      });
    });

    setMemberAccesses(accesses);
  }, [assignees]);

  const handleTasksReassignClick = () => {
    const verified = verifyAccess();
    if (!verified) {
      showErrorMessage('Kindly assign permissions & roles to the all task assignees');
      return;
    }
    reassignTasks();
  };

  const reassignTasks = async () => {
    setReassigning(true);
    try {
      const finalAssignees: TaskTransactionAccess[] = memberAccesses
        .map((access) => ({
          ...access,
          Tasks: access.Tasks.filter((task) => task.FullAccess !== null && task.RoleId !== null),
        }))
        .filter((access) => !!access.Tasks.length);

      if (!finalAssignees.length) {
        showErrorMessage('No valid assignees found.');
        setReassigning(false);
        return;
      }

      const payload = convertStateToReassignmentPayload(finalAssignees);

      const response = await reassignTasksInBulk(payload);
      if (response.status === 200) {
        handleClose(true);
        showSuccessMessage('Tasks reassigned.');
      }
    } catch (e) {
      showErrorMessage('Unable to reassign tasks');
    }
    setReassigning(false);
  };

  const verifyAccess = (): boolean => {
    let verified = memberAccesses.every((access) =>
      access.Tasks.every((task) => task.FullAccess !== null && task.RoleId !== null),
    );

    return verified;
  };

  const handleSelect = (value) => {
    setInputVal('');
    const numValue = Number(value);
    if (value) setAssignees((prev) => [...prev, numValue]);
  };

  const getFinalList = useCallback((): any[] => {
    const finalList: any[] = [];

    teamListWithoutContact.forEach((item) => {
      const found = assignees.find((assignee) => assignee === item.id);
      if (!found) finalList.push(item);
    });

    return finalList;
  }, [activeTeamList, assignees]);

  const convertStateToReassignmentPayload = (
    input: TaskTransactionAccess[],
  ): TaskReassignmentPayloadType[] => {
    const taskMap = new Map<
      string,
      {
        TaskId: number;
        TransactionId: number;
        Assignees: {
          AssigneeId: number;
          FullAccess: boolean;
          AddAsParticipant: boolean;
          RoleId: null | number;
        }[];
      }
    >();

    for (const entry of input) {
      for (const task of entry.Tasks) {
        const key = `${task.TaskId}_${task.TransactionId}`;

        if (!taskMap.has(key)) {
          taskMap.set(key, {
            TaskId: task.TaskId,
            TransactionId: task.TransactionId,
            Assignees: [],
          });
        }

        const taskGroup = taskMap.get(key)!;
        taskGroup.Assignees.push({
          AssigneeId: entry.MemberId,
          FullAccess: task.FullAccess ?? false,
          AddAsParticipant: task.AddAsParticipant,
          RoleId: task.RoleId,
        });
      }
    }

    return Array.from(taskMap.values());
  };

  const handleRemoveAssignee = (assigneeId) => {
    setAssignees((prev) => prev.filter((p) => p !== assigneeId));
  };

  const grantAccessToAssignee = (assigneeId) => {
    if (!trxCoordinatorRoleId) {
      showErrorMessage('Transaction Coordinator role not found.');
      return;
    }

    const newAccesses: TaskTransactionAccess[] = memberAccesses.map((access) => {
      if (access.MemberId !== assigneeId) return access;

      const newPermissions = {
        MemberId: assigneeId,
        Tasks: access.Tasks.map((task) => ({
          ...task,
          FullAccess: true,
          RoleId: trxCoordinatorRoleId,
        })),
      };
      return newPermissions;
    });

    setMemberAccesses(newAccesses);
  };

  return (
    <Modal
      className={styles.reassignModal}
      footer={null}
      width={720}
      open={open}
      onCancel={() => handleClose(false)}
      destroyOnClose
      closable={true}
      zIndex={1000}
    >
      <div className={styles.modalContent}>
        <div className={styles.header}>
          <div></div>
          <div>Reassign Tasks</div>
          <div className={styles.close} onClick={() => handleClose(false)}>
            <CloseModalIconSmall />
          </div>
        </div>
        <div className={styles.content}>
          <div className={styles.text}>Reassign all selected tasks to:</div>
          <div className={styles.searchInput}>
            <Icons variant={Icons.ASSIGNEES} />
            <AutoComplete
              onChange={(val) => setInputVal(val)}
              popupClassName={styles.autoCompleteTagDropdown}
              placeholder={'Select participant(s)'}
              value={inputVal}
              onSelect={handleSelect}
              className={styles.assigneeAutoComplete}
              filterOption={(inputValue, option) =>
                caseInsensitiveFilter(inputValue, option?.label as string)
              }
              clearIcon={<Cross color="#747475" />}
              allowClear
              onClear={() => setInputVal('')}
            >
              {getFinalList().length ? (
                getFinalList().map((item) => (
                  <Option
                    key={item.id}
                    label={`${item.firstName}${item.lastName || ''}`}
                    value={item.id}
                  >
                    <div className={styles.assigneeOptions}>
                      <Avatar
                        className={styles.avatarWrap}
                        avatarClassName={styles.avatar}
                        src={item.avatarUrl}
                        placeholder={convertNameToAvatarPlaceholder(
                          `${item.firstName}${item.lastName ? ` ${item.lastName}` : ''}`,
                        )}
                      />
                      <div className={styles.right}>
                        <div className={styles.name}>{`${item.firstName}${
                          item.lastName ? ` ${item.lastName}` : ''
                        }`}</div>
                        <div className={styles.role}>
                          {!item.teamRole || item.teamRole === 'Basic' ? 'Agent' : item.teamRole}
                        </div>
                      </div>
                    </div>
                  </Option>
                ))
              ) : (
                <Option label={'No Member Found'} value={''} className={styles.noMemberOption}>
                  <div className={styles.autoCompleteOption}>
                    <div className={styles.noResultsText}>No results found.</div>
                  </div>
                </Option>
              )}
            </AutoComplete>
          </div>
          {assignees.length ? (
            <div className={styles.assignees}>
              {memberAccesses.map((item) => {
                const assignee = teamListWithoutContact.find(
                  (contact) => contact.id === item.MemberId,
                );

                const inaccessibleTransactions = item.Tasks.filter(
                  (task) => task.AddAsParticipant,
                ).map((task) => ({
                  trxId: task.TransactionId,
                  trxName: task.TransactionName,
                }));

                const accessGranted = item.Tasks.every(
                  (task) => task.FullAccess !== null && task.RoleId !== null,
                );

                return (
                  <div className={styles.assignee}>
                    <div className={styles.left}>
                      <Avatar
                        className={styles.avatarWrap}
                        avatarClassName={styles.avatar}
                        src={assignee.avatarUrl}
                        placeholder={convertNameToAvatarPlaceholder(
                          `${assignee.firstName}${
                            assignee.lastName ? ` ${assignee.lastName}` : ''
                          }`,
                        )}
                      />
                      {inaccessibleTransactions.length ? (
                        <div className={styles.info}>
                          <div className={styles.name}>
                            {`${assignee.firstName}${
                              assignee.lastName ? ` ${assignee.lastName}` : ''
                            }`}
                            <span>{` doesn’t have access to`}</span>
                          </div>
                          {inaccessibleTransactions.map((trx) => (
                            <div className={styles.email}>{trx.trxName}</div>
                          ))}
                        </div>
                      ) : (
                        <div className={styles.info}>
                          <div className={styles.name}>{`${assignee.firstName}${
                            assignee.lastName ? ` ${assignee.lastName}` : ''
                          }`}</div>
                          <div className={styles.email}>{assignee.email}</div>
                        </div>
                      )}
                    </div>
                    {inaccessibleTransactions.length ? (
                      <div className={styles.accessControls}>
                        <Button
                          type="submit"
                          className={styles.skipButton}
                          title={'Skip'}
                          onClick={() => handleRemoveAssignee(assignee.id)}
                        />
                        <Button
                          type="submit"
                          className={styles.accessButton}
                          title={accessGranted ? 'Access Granted' : 'Give Access'}
                          onClick={() => grantAccessToAssignee(assignee.id)}
                          disabled={accessGranted}
                        />
                      </div>
                    ) : (
                      <div
                        className={styles.right}
                        onClick={() => handleRemoveAssignee(assignee.id)}
                      >
                        <Icons variant={Icons.CLOSE} color={'#676767'} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : null}
        </div>
        <div className={styles.footer}>
          <Button
            type="submit"
            className={styles.reassignButton}
            title={'Reassign'}
            onClick={handleTasksReassignClick}
            disabled={reassigning || !memberAccesses.length}
            isPending={reassigning}
          />
        </div>
      </div>
    </Modal>
  );
};

export default ReassignTaskModal;
